import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /**
   * One clamp at a time (cart drawer + cart page both listen on `document` for cart:update).
   * @type {boolean}
   */
  static #globalMaxPurchaseClamp = false;

  /** @type {number | undefined} */
  #maxPurchaseOuterRaf;

  /** @type {number | undefined} */
  #maxPurchaseInnerRaf;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onAnyCartUpdateValidateMaxPurchase);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.addEventListener('quantity-selector:purchase-limit-reached', this.#onPurchaseLimitReached);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onAnyCartUpdateValidateMaxPurchase);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.removeEventListener('quantity-selector:purchase-limit-reached', this.#onPurchaseLimitReached);
    this.#cancelScheduledMaxPurchaseClamp();
  }

  /**
   * Shows the existing per-line max-purchase error when the user attempts to
   * exceed the limit on a cart line (no cart-line sum overflow needed).
   * @param {Event} event
   */
  #onPurchaseLimitReached = (event) => {
    const detail = /** @type {CustomEvent<{ cartLine: number; message: string }>} */ (event).detail;
    if (!detail) return;
    const { cartLine, message } = detail;
    if (!cartLine || !message) return;
    this.#showCartLinePurchaseLimitMessage(cartLine, message);
  };

  #cancelScheduledMaxPurchaseClamp() {
    if (this.#maxPurchaseOuterRaf != null) cancelAnimationFrame(this.#maxPurchaseOuterRaf);
    if (this.#maxPurchaseInnerRaf != null) cancelAnimationFrame(this.#maxPurchaseInnerRaf);
    this.#maxPurchaseOuterRaf = undefined;
    this.#maxPurchaseInnerRaf = undefined;
  }

  /**
   * Horizon dispatches CartAddEvent / CartUpdateEvent as `Event` subclasses (not CustomEvent) while still attaching `detail`.
   * @param {Event} event
   */
  #onAnyCartUpdateValidateMaxPurchase = (event) => {
    if (CartItemsComponent.#globalMaxPurchaseClamp) return;
    const detail = /** @type {Event & { detail?: { data?: { source?: string } } }} */ (event).detail;
    const source = detail?.data?.source;
    if (source === 'cart-items-component-max-purchase') return;
    this.#scheduleValidateMaxPurchaseAfterRefresh();
  };

  /** Runs clamp right after morph/paint — no idle 120ms wait, no GET /cart.js round-trip. */
  #scheduleValidateMaxPurchaseAfterRefresh() {
    this.#cancelScheduledMaxPurchaseClamp();
    this.#maxPurchaseOuterRaf = requestAnimationFrame(() => {
      this.#maxPurchaseOuterRaf = undefined;
      this.#maxPurchaseInnerRaf = requestAnimationFrame(() => {
        this.#maxPurchaseInnerRaf = undefined;
        void this.#clampMaxPurchaseTotalsFromDom();
      });
    });
  }

  /** Read visible cart rows (`data-key` + quantity inputs); build /cart/update.js payload when totals exceed caps. */
  async #clampMaxPurchaseTotalsFromDom() {
    if (CartItemsComponent.#globalMaxPurchaseClamp) return;

    /** @type {Map<string, { limit: number, message: string }>} */
    const limitsByProductId = new Map();
    const selectors = this.querySelectorAll('quantity-selector-component[data-max-purchase-limit]');
    for (const el of selectors) {
      if (!(el instanceof HTMLElement)) continue;
      const pid = el.dataset.productId;
      const lim = el.dataset.maxPurchaseLimit;
      const msg = el.dataset.maxPurchaseMessage ?? '';
      if (!pid || lim == null || lim === '') continue;
      const n = parseInt(lim, 10);
      if (Number.isNaN(n)) continue;
      if (!limitsByProductId.has(pid)) limitsByProductId.set(pid, { limit: n, message: msg });
    }
    if (limitsByProductId.size === 0) return;

    const domLines = this.#collectPurchaseQtyRowsFromDom();
    if (domLines.length === 0) return;

    /** @type {Record<string, number>} */
    const aggregatedUpdates = {};
    /** @type {string | undefined} */
    let firstMessage;
    /** @type {number | undefined} */
    let firstLineForMessage;

    for (const [productIdStr, meta] of limitsByProductId) {
      const productLines = domLines.filter((row) => row.productId === productIdStr);
      const totalQty = productLines.reduce((sum, row) => sum + row.qty, 0);
      if (totalQty <= meta.limit) continue;

      if (!firstMessage) firstMessage = meta.message;

      let remaining = meta.limit;
      for (const row of productLines) {
        const newQ = Math.min(row.qty, remaining);
        remaining -= newQ;
        if (newQ !== row.qty) aggregatedUpdates[row.key] = newQ;
      }
    }

    if (Object.keys(aggregatedUpdates).length === 0) return;

    const keysForMsg = Object.keys(aggregatedUpdates);
    firstLineForMessage =
      keysForMsg.length > 0 ? this.#findCartLineForItemKey(keysForMsg[0] ?? '') : undefined;

    CartItemsComponent.#globalMaxPurchaseClamp = true;
    this.#disableCartItems();

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((comp) => {
      if (comp instanceof HTMLElement && comp.dataset.sectionId) {
        sectionsToUpdate.add(comp.dataset.sectionId);
      }
    });

    const updateBase = Theme.routes.cart_update_url;
    const updateUrl = updateBase.endsWith('.js') ? updateBase : `${updateBase}.js`;

    try {
      const body = JSON.stringify({
        updates: aggregatedUpdates,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      const responseText = await fetch(updateUrl, fetchConfig('json', { body })).then((r) => r.text());
      const parsedResponseText = JSON.parse(responseText);

      resetShimmer(this);

      if (parsedResponseText.errors) {
        console.error(parsedResponseText.errors);
        return;
      }

      if (firstMessage && firstLineForMessage != null) {
        this.#showCartLinePurchaseLimitMessage(firstLineForMessage, firstMessage);
      }

      const sections = parsedResponseText.sections;
      const html = sections?.[this.sectionId];

      const newCartHiddenItemCount = html
        ? new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="cartItemCount"]')?.textContent
        : undefined;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component-max-purchase',
          sections,
        })
      );

      if (html) morphSection(this.sectionId, html);
    } catch (e) {
      console.error(e);
    } finally {
      CartItemsComponent.#globalMaxPurchaseClamp = false;
      this.#enableCartItems();
    }
  }

  /**
   * @returns {{ key: string; productId: string; qty: number }[]}
   */
  #collectPurchaseQtyRowsFromDom() {
    /** @type {{ key: string; productId: string; qty: number }[]} */
    const rows = [];
    for (const tr of this.querySelectorAll('tr.cart-items__table-row[data-key]')) {
      if (!(tr instanceof HTMLElement)) continue;
      const key = tr.dataset.key;
      if (!key) continue;

      const comp = tr.querySelector('quantity-selector-component');
      const input = comp?.querySelector('input[type="number"]');
      if (!(comp instanceof HTMLElement) || !comp.dataset.productId || !(input instanceof HTMLInputElement)) continue;

      const qty = parseInt(input.value, 10) || 0;
      rows.push({ key, productId: comp.dataset.productId, qty });
    }
    return rows;
  }

  /** @param {string} itemKey line_item.key */
  #findCartLineForItemKey(itemKey) {
    const row = this.querySelector(`tr[data-key="${CSS.escape(itemKey)}"]`);
    const input = row?.querySelector('input[type="number"][data-cart-line]');
    if (!(input instanceof HTMLInputElement)) return undefined;
    const line = Number(input.dataset.cartLine);
    return Number.isNaN(line) ? undefined : line;
  }

  /**
   * @param {number} line - 1-based
   * @param {string} message
   */
  #showCartLinePurchaseLimitMessage(line, message) {
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (cartItemError instanceof HTMLElement) cartItemError.textContent = message;
    if (cartItemErrorContainer instanceof HTMLElement) {
      cartItemErrorContainer.classList.remove('hidden');
      window.setTimeout(() => {
        cartItemErrorContainer.classList.add('hidden');
      }, 10000);
    }
  }

  /**
   * @param {string} productId
   * @param {number} excludeCartLine - 1-based cart line index to exclude
   */
  #sumQuantityForProduct(productId, excludeCartLine) {
    let sum = 0;
    for (const row of this.refs.cartItemRows) {
      const comp = row.querySelector('quantity-selector-component');
      const input = comp?.querySelector('input[type="number"]');
      if (!comp || !input || !(comp instanceof HTMLElement) || !(input instanceof HTMLInputElement)) continue;
      if (comp.dataset.productId !== String(productId)) continue;
      const lineNum = Number(input.dataset.cartLine);
      if (lineNum === excludeCartLine) continue;
      sum += parseInt(input.value, 10) || 0;
    }
    return sum;
  }

  /**
   * @param {number} line - 1-based
   * @param {string} message
   * @param {HTMLElement | undefined} quantitySelectorEl
   */
  #rejectMaxPurchaseQuantityChange(line, message, quantitySelectorEl) {
    if (!quantitySelectorEl) return;
    const quantityInput = quantitySelectorEl.querySelector('input[type="number"]');
    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = quantityInput.defaultValue;
      const select = quantitySelectorEl.querySelector('select.js-qty-dropdown');
      if (select instanceof HTMLSelectElement) {
        select.value = quantityInput.value;
      }
    }

    this.#showCartLinePurchaseLimitMessage(line, message);
  }

  /**
   * @param {QuantitySelectorUpdateEvent} event
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    const quantitySelectorEl = this.refs.quantitySelectors[line - 1];
    const limitEl = quantitySelectorEl instanceof HTMLElement ? quantitySelectorEl : undefined;
    const purchaseLimit = limitEl?.dataset?.maxPurchaseLimit;
    const purchaseMessage = limitEl?.dataset?.maxPurchaseMessage;
    const productId = limitEl?.dataset?.productId;
    if (purchaseLimit != null && purchaseLimit !== '' && purchaseMessage && productId) {
      const mf = parseInt(purchaseLimit, 10);
      if (!Number.isNaN(mf)) {
        const others = this.#sumQuantityForProduct(productId, line);
        if (others + quantity > mf) {
          this.#rejectMaxPurchaseQuantityChange(line, purchaseMessage, limitEl);
          return;
        }
      }
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
